import {
  Controller,
  UseFormReturn,
  useFormState,
  useWatch,
} from "react-hook-form";
import { SearchPreviewFormType } from "../../types";
import { useI18n } from "@/contexts/i18nContext";
import {
  getTimePeriodFromDate,
  usePredefinedTimeIntervals,
} from "../../helpers";
import { Field, FieldLabel, FieldError } from "@components/ui/field";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarIcon, ChevronDownIcon, XIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Button } from "@components/ui/button";
import DatePicker from "@components/ui/date-picker";

export default function TimePeriodField({
  form,
}: {
  form: UseFormReturn<SearchPreviewFormType>;
}) {
  const { formatDate } = useI18n();
  const { t } = useLingui();
  const predefinedTimeIntervals = usePredefinedTimeIntervals();

  const startDate = useWatch({
    control: form.control,
    name: "start_date",
  });

  const endDate = useWatch({
    control: form.control,
    name: "end_date",
  });

  const { errors } = useFormState({
    control: form.control,
    name: ["start_date", "end_date"],
  });
  const fieldError = errors.start_date ?? errors.end_date;

  const timePeriodId = !endDate ? getTimePeriodFromDate(startDate) : undefined;
  const timePeriod = predefinedTimeIntervals.find(
    (pti) => pti.value.id === timePeriodId
  );

  return (
    <Field data-invalid={!!fieldError}>
      <FieldLabel htmlFor="time_period">
        <Trans>Tidsperiod</Trans>
      </FieldLabel>

      <Popover modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="text-left justify-start font-normal w-full"
            id="time_period"
            aria-invalid={!!fieldError}
          >
            <CalendarIcon />
            {timePeriod ? (
              timePeriod.label
            ) : startDate ? (
              [startDate, endDate]
                .filter(Boolean)
                .map((date) => formatDate(date, "PPP"))
                .join(" - ")
            ) : (
              <Trans>Välj en tidsperiod</Trans>
            )}
            <ChevronDownIcon className="text-muted-foreground opacity-50 ml-auto" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <div className="flex flex-col">
            <div className="py-2 px-3 border-b">
              <p className="text-sm font-medium">
                <Trans>Fördefinierade</Trans>
              </p>
            </div>

            <RadioGroup
              className="p-3.5 border-b"
              value={timePeriodId ?? ""}
              onValueChange={(newTimePeriodId) => {
                const newTimePeriod = predefinedTimeIntervals.find(
                  (pti) => pti.value.id === newTimePeriodId
                );

                if (!newTimePeriod) {
                  return;
                }

                form.setValue("start_date", newTimePeriod.value.date, {
                  shouldValidate: true,
                  shouldDirty: true,
                });

                form.setValue("end_date", null, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            >
              {predefinedTimeIntervals.map((timeInterval) => {
                return (
                  <div
                    key={timeInterval.value.id}
                    className="flex items-center gap-2.5"
                  >
                    <RadioGroupItem
                      value={timeInterval.value.id}
                      id={timeInterval.value.id}
                    />
                    <label className="text-sm" htmlFor={timeInterval.value.id}>
                      {timeInterval.label}
                    </label>
                  </div>
                );
              })}
            </RadioGroup>

            <div className="flex flex-col gap-3 p-3.5">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">
                  <Trans>Eget tidsintervall</Trans>
                </p>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Välj ett datumintervall för att visa resultat från en
                    specifik period
                  </Trans>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2.75">
                <Controller
                  name="start_date"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = "start-date";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Startdatum</Trans>
                        </FieldLabel>
                        <DatePicker
                          onChangeDate={(newStartDate) => {
                            if (newStartDate) {
                              field.onChange(newStartDate);
                              form.clearErrors("start_date");
                            }
                          }}
                          onInvalidDate={(_value) => {
                            form.setError("start_date", {
                              type: "manual",
                              message: t`${_value} är inte ett giltigt datum.`,
                            });
                          }}
                          value={field.value}
                          max={endDate ?? undefined}
                          id={id}
                        />
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  name="end_date"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    const id = "end-date";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Slutdatum</Trans>
                        </FieldLabel>
                        <div className="flex gap-2">
                          <DatePicker
                            onChangeDate={(newEndDate) => {
                              field.onChange(newEndDate);
                              form.clearErrors("end_date");
                            }}
                            onInvalidDate={(_value) => {
                              form.setError("end_date", {
                                type: "manual",
                                message: t`${_value} är inte ett giltigt datum.`,
                              });
                            }}
                            value={field.value ?? undefined}
                            min={startDate}
                            id={id}
                          />
                          {field.value && (
                            <Button
                              size="icon"
                              variant="outline"
                              type="button"
                              onClick={() => {
                                field.onChange(null);
                              }}
                            >
                              <span className="sr-only">
                                <Trans>Ta bort slutdatum</Trans>
                              </span>
                              <XIcon />
                            </Button>
                          )}
                        </div>
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {fieldError && <FieldError errors={[fieldError]} />}
    </Field>
  );
}
